import { DragEndEvent, DragOverEvent, DragStartEvent } from '@dnd-kit/core';
import { parseSectionItemId, SectionItemRef } from '@griply/utils';
import { useCallback, useEffect, useRef, useState } from 'react';
import { ID } from 'shared/types/id';
import { SectionBase, SectionItemBase } from 'shared/types/orderable-section';
import { blurActiveElement } from 'shared/utils/blur-active-element';
import {
  handleItemBetweenSectionsMovement,
  handleItemWithinSectionMovement,
} from 'shared/utils/handle-section-item-movement';

export type SectionItemMove = {
  sourceSectionId: ID;
  targetSectionId: ID;
};

// Sortable ids are occurrence ids (`<sectionId>/<itemId>`) — only the picked-up occurrence moves.
export const useDroppableSectionControls = <
  Item extends SectionItemBase,
  Section extends SectionBase<Item>,
>({
  initialSections,
  onDragEnd,
}: {
  initialSections: Section[];
  onDragEnd?: (
    sections: Section[],
    movedItem: Item,
    move: SectionItemMove,
  ) => void;
}) => {
  const [currentSections, setCurrentSections] =
    useState<Section[]>(initialSections);
  const [activeItemId, setActiveItemId] = useState<ID>();
  // where the occurrence was picked up, and where it currently sits mid-drag
  const dragRef = useRef<(SectionItemRef & { sourceSectionId: ID }) | null>(
    null,
  );

  useEffect(() => {
    setCurrentSections(initialSections);
  }, [initialSections]);

  const handleDragStart = useCallback(({ active }: DragStartEvent) => {
    const ref = parseSectionItemId(active.id);
    if (!ref) {
      return;
    }
    dragRef.current = { ...ref, sourceSectionId: ref.sectionId };
    setActiveItemId(ref.itemId);
  }, []);

  const handleDragOver = useCallback(({ active, over }: DragOverEvent) => {
    const drag = dragRef.current;
    if (!over?.id || over.id === active.id || !drag) {
      return;
    }

    setCurrentSections((oldSections) => {
      const { sections, targetSectionId } = handleItemBetweenSectionsMovement(
        oldSections,
        drag,
        over.id,
      );
      if (targetSectionId) {
        dragRef.current = { ...drag, sectionId: targetSectionId };
      }
      return sections;
    });
  }, []);

  const handleDragEnd = useCallback(
    ({ over }: DragEndEvent) => {
      const drag = dragRef.current;
      dragRef.current = null;
      setActiveItemId(undefined);
      blurActiveElement();
      if (!over?.id || !drag) {
        return;
      }

      const { sections, activeItem } = handleItemWithinSectionMovement<
        Item,
        Section
      >(currentSections, drag, over.id);

      if (!activeItem) {
        return;
      }

      return onDragEnd?.(sections, activeItem, {
        sourceSectionId: drag.sourceSectionId,
        targetSectionId: drag.sectionId,
      });
    },
    [currentSections, onDragEnd],
  );

  const handleDragCancel = useCallback(() => {
    dragRef.current = null;
    setActiveItemId(undefined);
    setCurrentSections(initialSections);
  }, [initialSections]);

  return {
    sections: currentSections,
    activeId: activeItemId,
    handleDragStart,
    handleDragOver,
    handleDragEnd,
    handleDragCancel,
    setSections: setCurrentSections,
  };
};
